import * as THREE from 'three'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import type { ClashResult } from '@viewer/clash/lib'

// ---------------------------------------------------------------------------
// Navegar ate um conflito: camara, seleccao, isolamento e volta atras.
//
// A camara enquadra a ZONA do conflito (`bbox`, em mundo), nao os dois
// elementos inteiros: numa viga de 8 m a atravessar um pilar, enquadrar
// os elementos deixava o no do conflito com dois pixeis. A caixa e
// inflada para nao ficar colada a geometria e tem um minimo, porque uma
// interseccao de 2 mm dava uma caixa degenerada e a camara mergulhava
// dentro do betao.
//
// A vista anterior guarda-se a PRIMEIRA vez que se navega (nao a cada
// clique): quem percorre dez conflitos seguidos quer voltar ao sitio de
// onde partiu, nao ao conflito anterior.
// ---------------------------------------------------------------------------

/** Margem a volta da zona do conflito, em metros. */
const FRAME_PAD_M = 0.75
/** Aresta minima da caixa enquadrada, em metros. */
const FRAME_MIN_M = 1.5

interface CameraPose {
  position: THREE.Vector3
  target: THREE.Vector3
}

let savedPose: CameraPose | null = null

export function hasPreviousView(): boolean {
  return savedPose !== null
}

export function clearPreviousView(): void {
  savedPose = null
}

function currentPose(): CameraPose | null {
  const world = viewerApi.getWorld()
  if (!world) return null
  try {
    const target = new THREE.Vector3()
    world.camera.controls.getTarget(target)
    return { position: world.camera.three.position.clone(), target }
  } catch {
    return null
  }
}

/** Caixa a enquadrar: a zona do conflito, inflada e com aresta minima. */
export function frameBox(r: ClashResult): THREE.Box3 {
  const min = new THREE.Vector3(...r.bbox.min)
  const max = new THREE.Vector3(...r.bbox.max)
  const box = new THREE.Box3(min, max)
  if (box.isEmpty()) {
    box.setFromCenterAndSize(
      new THREE.Vector3(...r.point),
      new THREE.Vector3(FRAME_MIN_M, FRAME_MIN_M, FRAME_MIN_M),
    )
    return box
  }
  box.expandByScalar(FRAME_PAD_M)
  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())
  box.setFromCenterAndSize(
    center,
    new THREE.Vector3(
      Math.max(size.x, FRAME_MIN_M),
      Math.max(size.y, FRAME_MIN_M),
      Math.max(size.z, FRAME_MIN_M),
    ),
  )
  return box
}

/** Os dois elementos do conflito, no formato da seleccao do viewer. */
export function clashTargets(
  r: ClashResult,
): { modelId: string; localId: number }[] {
  return [
    { modelId: r.pair.modelA, localId: r.pair.localIdA },
    { modelId: r.pair.modelB, localId: r.pair.localIdB },
  ]
}

/**
 * Vai ao conflito: enquadra a zona, selecciona os dois elementos e pinta
 * o realce. Best-effort em tudo o que depende do 3D estar montado.
 */
export async function goToClash(r: ClashResult): Promise<void> {
  const world = viewerApi.getWorld()
  if (!savedPose) savedPose = currentPose()

  const targets = clashTargets(r)
  const store = useAppStore.getState()
  store.applyBulkSelection(targets, 'replace')

  const highlighter = viewerApi.getHighlighter()
  if (highlighter) {
    const payload: Record<string, Set<number>> = {}
    for (const t of targets) {
      ;(payload[t.modelId] ??= new Set<number>()).add(t.localId)
    }
    try {
      await highlighter.highlightByID('select', payload, true, false)
    } catch (err) {
      console.debug('[clash] highlightByID ignorado', err)
    }
  }

  if (!world) return
  try {
    await world.camera.controls.fitToBox(frameBox(r), true, {
      paddingTop: 0.15,
      paddingBottom: 0.15,
      paddingLeft: 0.15,
      paddingRight: 0.15,
    })
  } catch (err) {
    console.warn('[clash] fitToBox falhou', err)
  }
}

/** Esconde tudo o resto. Reutiliza o isolamento do viewer, que ja trata
 *  do historico (o "Anular" da barra desfaz isto). */
export async function isolateClash(r: ClashResult): Promise<void> {
  useAppStore.getState().applyBulkSelection(clashTargets(r), 'replace')
  await viewerApi.isolateSelected()
}

export async function showAllAgain(): Promise<void> {
  await viewerApi.showAll()
}

/** Volta a camara ao sitio de onde se partiu. */
export async function restorePreviousView(): Promise<void> {
  const world = viewerApi.getWorld()
  const pose = savedPose
  savedPose = null
  if (!world || !pose) return
  try {
    await world.camera.controls.setLookAt(
      pose.position.x,
      pose.position.y,
      pose.position.z,
      pose.target.x,
      pose.target.y,
      pose.target.z,
      true,
    )
  } catch (err) {
    console.warn('[clash] setLookAt falhou', err)
  }
}
